import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  CLAIM_WORKSPACE_PREFETCH_TTL_SECONDS,
  useClaimWorkspacePrefetch,
} from '../../../utils/claimWorkspacePrefetch';

/**
 * Home's half of the workbench warm-up (GDN-3201's optimization, brought to
 * the Home cards).
 *
 * The Claims worklist has warmed the workbench's first fetch wave on row
 * hover since GDN-3201 — the claim-workspace GET (~2.35s at the production
 * median, GDN-3199), the claim record, and the certification. Home opens the
 * SAME workbench from the same kind of row and did not, so a biller who
 * arrives on the default tab pays the cold price the worklist stopped paying.
 *
 * Home has three moments where a claim is about to be opened, not one, which
 * is why this is its own hook rather than a second caller of
 * `useWorklistRowPrefetch`:
 *
 * - a card ROW under the pointer, exactly as on the worklist;
 * - the card's "Work this queue" BUTTON under the pointer, which opens the
 *   run's first step — a target the worklist has no equivalent of;
 * - ARRIVAL, which warms each queue's first step without waiting for a
 *   pointer at all (see `useHomeArrivalPrefetch`).
 *
 * All three share one seen-set, so the button hover after a row hover on the
 * same claim costs nothing, and the arrival warm makes the later hover a
 * no-op rather than a second request. The requests themselves are
 * `useClaimWorkspacePrefetch`'s, so this never invents cache entries: a real
 * request warms the real cache, or nothing happens.
 */

/** Don't re-request data fresher than this. Shared with the worklist's hover
 *  prefetch, and for the same reason: warm a COLD cache for an imminent
 *  click, not keep a hot one hot. */
export const HOME_PREFETCH_TTL_SECONDS = CLAIM_WORKSPACE_PREFETCH_TTL_SECONDS;

/** The two facts a warm needs. Any Home row shape carries them. */
export interface HomePrefetchTarget {
  claim_id: string;
  encounter_id?: string | null;
}

export interface HomeClaimPrefetch {
  /**
   * The pointer is on a row, or on the button that opens this claim. Skipped
   * on touch pointers, where the "hover" IS the tap: prefetching there fires
   * alongside the navigation it was meant to precede.
   */
  prefetchOnHover: (target: HomePrefetchTarget) => void;
  /**
   * Warm without a pointer — the arrival prefetch. Not hover-gated, because
   * nothing here is a hover: on a touch pointer this is the only warm the
   * claim will ever get, and it is the one that helps most.
   */
  warm: (target: HomePrefetchTarget) => void;
  /**
   * Whether a warm was ATTEMPTED for this claim this mount — the `prefetched`
   * flag on billing_home_queue_selected, the optimization's own meter (hit
   * rate, not just adoption).
   */
  wasPrefetched: (claimId: string) => boolean;
}

export function useHomeClaimPrefetch(): HomeClaimPrefetch {
  const prefetchClaimWorkspace = useClaimWorkspacePrefetch();

  /* Evaluated once per mount: pointer capability doesn't change mid-session,
     and a matchMedia call per hover would be waste. `?? false` covers jsdom
     and any environment without matchMedia — no hover, no hover prefetch. */
  const canHover = useMemo(
    () => window.matchMedia?.('(hover: hover) and (pointer: fine)').matches ?? false,
    [],
  );
  /* Once per claim per mount. Five cards can show the same claim only by
     accident, but one card's row and its "Work this queue" button routinely
     name the SAME claim, and a sweep across the grid must not re-fire on
     every enter/leave/enter. The TTL alone cannot stop that burst. */
  const seenRef = useRef(new Set<string>());

  const warm = useCallback(
    (target: HomePrefetchTarget) => {
      if (seenRef.current.has(target.claim_id)) return;
      seenRef.current.add(target.claim_id);
      prefetchClaimWorkspace({
        claimId: target.claim_id,
        encounterId: target.encounter_id ?? null,
      });
    },
    [prefetchClaimWorkspace],
  );

  const prefetchOnHover = useCallback(
    (target: HomePrefetchTarget) => {
      if (!canHover) return;
      warm(target);
    },
    [canHover, warm],
  );

  const wasPrefetched = useCallback((claimId: string) => seenRef.current.has(claimId), []);

  return useMemo(
    () => ({ prefetchOnHover, warm, wasPrefetched }),
    [prefetchOnHover, warm, wasPrefetched],
  );
}

/**
 * The arrival warm: each queue's FIRST STEP, warmed while Home settles.
 *
 * A hover only buys the dwell between pointing and clicking — a few hundred
 * milliseconds against a ~2.35s workspace read. The five claims at the top of
 * the five queues are the ones "Work this queue" opens and the ones a card
 * row click most often lands on, so warming them on arrival buys the whole
 * read instead of a slice of it.
 *
 * The cost is real and is why this is deliberately narrow: FOUR claims, never
 * the twenty on the cards, once per mount, and skipped for anything already
 * warmed or fresher than the TTL — a biller bouncing workbench → Home →
 * workbench re-fires nothing. The walk is sequential so the network carries
 * one speculative claim at a time behind the five card reads and the
 * queue-run pages, which are what actually render the page.
 *
 * And it runs in IDLE TIME ONLY: `requestIdleCallback` with no `timeout` and
 * no `setTimeout` fallback, which is the whole rule — spare time or none.
 * A timer fires under load exactly when the tab can least afford five
 * workspace reads, and a warm that arrives after the click was going to
 * arrive with it anyway. Two consequences are deliberate: a hidden tab,
 * where the browser withholds idle slots, warms nothing for a surface nobody
 * is looking at; and where `requestIdleCallback` is absent (Safari before
 * 18.2, jsdom) hover is the whole optimization — which is what every other
 * surface in the app has anyway.
 */

type CancelIdle = () => void;

/** Runs `run` in the next idle slot, or never, where there are none. */
const scheduleIdle = (run: () => void): CancelIdle => {
  if (typeof window.requestIdleCallback !== 'function') return () => undefined;
  const handle = window.requestIdleCallback(run);
  return () => window.cancelIdleCallback?.(handle);
};

/**
 * Warms `targets` one at a time, each in its own idle slot, and stops the
 * moment Home unmounts — a walk that outlived the page would be requesting
 * claims for a surface nobody is looking at.
 */
export function useHomeArrivalPrefetch(
  targets: readonly HomePrefetchTarget[],
  warm: (target: HomePrefetchTarget) => void,
): void {
  /* The effect reads the LATEST targets but re-runs only when the claims
     themselves change: `rowsByQueue` gets a new identity on every refetch,
     and rescheduling the walk on each one would be churn for no new claim. */
  const targetsRef = useRef(targets);
  targetsRef.current = targets;
  const key = targets.map(target => target.claim_id).join('|');

  useEffect(() => {
    if (targetsRef.current.length === 0) return undefined;
    let cancelled = false;
    let cancelPending: CancelIdle = () => undefined;
    const step = (index: number) => {
      const target = targetsRef.current[index];
      if (cancelled || !target) return;
      cancelPending = scheduleIdle(() => {
        if (cancelled) return;
        warm(target);
        step(index + 1);
      });
    };
    step(0);
    return () => {
      cancelled = true;
      cancelPending();
    };
  }, [key, warm]);
}
